import { useEffect, useMemo, useState } from "react";
import useTrackView from "../../hooks/useTrackView";
import { useParams } from "react-router-dom";
import { UserLayout } from "./components/layout/UserLayout";
import Container from "./components/Container";
import Text from "./components/Text";
import Img from "./components/Img";
import Effects from "./components/Effects";
import { getImg } from "../../Components/Utils";
import BlogCard from "./components/cards/BlogCard";
import PageHero from "./components/PageHero";
import { useFetchBlogDetailPageQuery } from "../../stores/api/pages.api";
import {
  Tag,
  ArrowLeft,
  ArrowRight,
  Link2,
  MessageCircle,
  Camera,
  Play,
  Briefcase,
} from "lucide-react";

const HISTORY_KEY = "blog_nav_history";

const readHistory = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const writeHistory = (list) => {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable */
  }
};

const useBlogNavHistory = (slug, title) => {
  const [history, setHistory] = useState(readHistory);

  useEffect(() => {
    if (!slug || !title) return;
    const current = readHistory();
    const idx = current.findIndex((e) => e.slug === slug);
    let next;
    if (idx >= 0) {
      next = [...current];
      next[idx] = { slug, title };
    } else {
      next = [...current, { slug, title }].slice(-50);
    }
    writeHistory(next);
    setHistory(next);
  }, [slug, title]);

  return useMemo(() => {
    const idx = history.findIndex((e) => e.slug === slug);
    if (idx === -1) return { prev: null, next: null };
    return {
      prev: idx > 0 ? history[idx - 1] : null,
      next: idx < history.length - 1 ? history[idx + 1] : null,
    };
  }, [history, slug]);
};

const SOCIAL_ICON_MAP = {
  link: Link2,
  message: MessageCircle,
  camera: Camera,
  play: Play,
  briefcase: Briefcase,
};

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const BlogDetailSkeleton = () => (
  <UserLayout>
    <Container variant="narrow" className="py-12">
      <Skeleton className="aspect-[16/9] w-full mb-8 rounded-xl" />
      <Skeleton className="h-8 w-3/4 mb-3" />
      <Skeleton className="h-4 w-32 mb-6" />
      <Skeleton className="h-20 w-full mb-6" />
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Skeleton className="aspect-[4/3]" />
        <Skeleton className="aspect-[4/3]" />
      </div>
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-2/3 mb-8" />
    </Container>
  </UserLayout>
);

export const BlogDetail = () => {
  const { slug } = useParams();
  const { data, isLoading } = useFetchBlogDetailPageQuery(slug);
  const { prev, next } = useBlogNavHistory(slug, data?.post?.title);
  useTrackView("blog", data?.post?.id);

  if (isLoading) return <BlogDetailSkeleton />;

  const post = data?.post;
  const related = data?.related ?? [];

  if (!post) {
    return (
      <UserLayout>
        <Container variant="narrow" className="py-20 text-center">
          <Text is="p" className="text-gray-500">Post not found</Text>
        </Container>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <PageHero
        title={"Blog : " + post.title}
        crumbs={[
          { label: "Blog", href: "/blog" },
          { label: post.tags?.[0] ?? "Article" },
        ]}
      />
      <Container variant="narrow" className="py-12">
        <Effects type="scroll-fadeup">
          {/* Hero image */}
          <div className="rounded-xl overflow-hidden aspect-[16/9] mb-8">
            <Img
              src={getImg(post.img)}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Title + meta */}
          <Text
            is="h2"
            variant="section-heading"
            className="text-gray-900 text-2xl md:text-3xl leading-snug mb-2"
          >
            {post.title}
          </Text>
          <Text is="p" className="text-xs text-gray-400 mb-6">
            {post.author} &nbsp;·&nbsp; {post.date}
          </Text>

          {/* Blockquote */}
          {post.blockquote && (
            <Text
              is="blockquote"
              className="border-l-4 border-gray-800 pl-4 my-6"
            >
              <Text
                is="p"
                variant="body"
                className="text-gray-700 italic leading-7"
              >
                {post.blockquote}
              </Text>
            </Text>
          )}

          {/* Inline images */}
          {post.images?.length > 0 && (
            <div className="grid grid-cols-2 gap-4 my-6">
              {post.images.map((img, i) => (
                <div key={i} className="rounded-lg overflow-hidden aspect-[4/3]">
                  <Img
                    src={getImg(img)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Body — HTML content */}
          <div
            className="prose prose-sm max-w-none text-gray-600 leading-7 mb-8 [&_p]:mb-4"
            dangerouslySetInnerHTML={{ __html: post.content ?? "" }}
          />

          {/* Tags + share */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={13} className="text-gray-400" />
              {(post.tags ?? []).map((tag) => (
                <Text
                  key={tag}
                  is="a"
                  href="#"
                  className="text-xs font-semibold border border-gray-300 px-3 py-1 hover:border-blue-500 hover:text-blue-500 transition-colors"
                >
                  {tag}
                </Text>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <Text
                is="span"
                className="text-xs font-semibold text-gray-400 mr-1"
              >
                Share:
              </Text>
              {(post.socialLinks ?? []).map((s, i) => {
                const Icon = SOCIAL_ICON_MAP[s.type] ?? Link2;
                return (
                  <Text
                    key={i}
                    is="a"
                    href={s.href}
                    className="w-7 h-7 border border-gray-300 flex items-center justify-center text-gray-500 hover:border-gray-900 hover:text-gray-900 transition-colors"
                  >
                    <Icon size={14} />
                  </Text>
                );
              })}
            </div>
          </div>

          {/* Prev / Next — driven by visit history */}
          {(prev || next) && (
            <div className="grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-gray-100">
              {prev ? (
                <Text
                  is="a"
                  href={`/blog/${prev.slug}`}
                  className="flex items-center gap-3 group"
                >
                  <div className="w-10 h-10 rounded-full border border-gray-900 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 group-hover:text-white transition-colors">
                    <ArrowLeft size={14} />
                  </div>
                  <div>
                    <Text
                      is="p"
                      className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5"
                    >
                      Previous
                    </Text>
                    <Text
                      is="h2"
                      className="text-lg font-bold text-gray-900 group-hover:text-blue-500 transition-colors leading-snug"
                    >
                      {prev.title}
                    </Text>
                  </div>
                </Text>
              ) : (
                <span />
              )}
              {next && (
                <Text
                  is="a"
                  href={`/blog/${next.slug}`}
                  className="flex items-center justify-end gap-3 text-right group ml-auto"
                >
                  <div>
                    <Text
                      is="p"
                      className="text-[10px] uppercase tracking-wider text-gray-400 mb-0.5"
                    >
                      Next
                    </Text>
                    <Text is="h2" variant="nav-post-title">
                      {next.title}
                    </Text>
                  </div>
                  <div className="w-10 h-10 rounded-full border border-gray-900 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 group-hover:text-white transition-colors">
                    <ArrowRight size={14} />
                  </div>
                </Text>
              )}
            </div>
          )}
        </Effects>

        {/* Related Articles */}
        {related.length > 0 && (
          <Effects type="scroll-fadeup">
            <div className="mt-16">
              <Text
                is="h3"
                variant="section-heading"
                className="text-gray-900 text-xl text-center mb-8"
              >
                Related Articles
              </Text>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {related.map((p, i) => (
                  <BlogCard
                    key={i}
                    img={p.img}
                    title={p.title}
                    description={p.description ?? ""}
                    href={`/blog/${p.slug}`}
                    category={p.category?.name ?? p.category ?? null}
                  />
                ))}
              </div>
            </div>
          </Effects>
        )}
      </Container>
    </UserLayout>
  );
};
